import { useApi } from "src/hooks/useApi";
import { useAuth } from "src/context/AuthProvider";
import { isAdmin, isDoctor } from "src/utils/roles";
import type { Doctor } from "src/types";

/**
 * Who an invoice belongs to when someone uploads or creates one — the rule from
 * backend services/invoiceOwner.resolveUploadOwner (memory "invoice owner rule"):
 * - Doctors and admins create for themselves (no choice shown) — unless granted "create for
 *   other doctors" (permCreateForOthers, Team Settings): then they may pick, defaulting to themselves.
 * - Staff (MedicalAssistant/Accounting) never own invoices: with several doctors they must pick
 *   one; with a single doctor it's that doctor.
 *
 * `picked` is the caller's current pick (state lives with the caller — a popup's useState, or the
 * manual-creation draft). `enabled: false` skips loading the doctors (e.g. the user can't create
 * invoices at all).
 */
export function useInvoiceOwnerChoice(picked: string, enabled = true) {
    const { user } = useAuth();
    const { data: doctorsData } = useApi<Doctor[]>(enabled ? "/doctors" : null);
    // Array.isArray, not just a nullish check — a malformed/non-array response (seen in one
    // test harness's catch-all mock, and plausible from a flaky API response too) must never
    // reach doctors.some()/doctors.map() as a non-array value.
    const doctors = (Array.isArray(doctorsData) ? doctorsData : [])
        .filter((d) => isDoctor(d.userRights) || isAdmin(d.userRights));
    const ownsInvoices = isDoctor(user?.doctor?.userRights) || isAdmin(user?.doctor?.userRights);
    /** A doctor/admin allowed to create for colleagues — picks optionally, defaults to themselves. */
    const mayPickOthers = ownsInvoices && user?.doctor?.permCreateForOthers === true;
    const showPicker = (!ownsInvoices || mayPickOthers) && doctors.length > 1;
    const ownDoctorId = user?.doctor?.id;
    const pickedValid = doctors.some((d) => d.id === picked);

    // What a pick-or-default resolves to — the uploader's own id when they're one of the doctors.
    const effectiveDoctorId = (pickedValid ? picked : "") || (doctors.some((d) => d.id === ownDoctorId) ? ownDoctorId! : "");

    // The owner the invoice will actually get (also covers staff in a single-doctor praxis, where
    // the upload leaves it to the server). Empty while staff still have to pick.
    const ownerId = ownsInvoices
        ? (mayPickOthers && pickedValid ? picked : (ownDoctorId ?? ""))
        : showPicker
          ? (pickedValid ? picked : "")
          : (doctors[0]?.id ?? "");
    const owner = doctors.find((d) => d.id === ownerId) ?? null;

    return {
        doctors,
        ownsInvoices,
        mayPickOthers,
        showPicker,
        effectiveDoctorId,
        ownerId,
        owner,
        doctorsLoaded: doctorsData !== null,
        /** Staff with several doctors haven't picked yet (a doctor allowed to pick never "misses" — it's them). */
        missing: showPicker && !effectiveDoctorId,
    };
}
